import React, { useContext, useEffect, useRef } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  every, find, flatten, includes, sortBy,
} from 'lodash-es';
import qs from 'qs';
import {
  shopOperatorsObjectAtom, userSettingsAtom, USER_SETTINGS_DEFAULTS,
} from '@shared/state/siteState';
import { routerStepsAtom } from '@shared/state/routingState';
import {
  bodyDataAtom, customerPartsAtom, neckDataAtom,
} from '@shared/state/pricingState';

import {
  partConfigTermsAtom,
  partConfigTermTypesAtom,
  partViewerConfigDataAtom,
  newCustomerPartsAtom,
} from '@shared/state/partViewState';
import useFirebase from '@app/providers/firebase';
import { AuthContext } from '@app/providers/firebase/AuthProvider';
import { useParams } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import {
  ncCorrectiveActionsObjectAtom,
  ncReasonsObjectAtom,
  ncRecordsAtom,
} from '@shared/state/utilState';
import { glCodesAtom, productCodesAtom, termsAtom } from '@shared/state/accounting';
import { ITerm } from '@shared/types/accounting';
import db, { getDb } from '@services/db';
import {
  accessToken, orderBillingAddress,
} from '@data/jb_api';
import {
  ICustomerRecord,
  IGlCode,
  IMaterialSpecification,
  INCRecord,
  IOrderItem,
  IPurchaseOrder,
  IQATicket,
  IShipment,
  IShippingAddress,
} from '@shared/types/dbRecords';
import {
  IContact,
} from '@shared/types/jb';
import { IProductCode } from '@shared/types/accounting';
import { inventoryItemsAtom } from '@shared/state/inventoryState';
import { IVendor } from '@shared/types/vendor';
import { vendorRecordsAtom } from '@shared/state/vendorState';
import {
  currentShopOrderAtom, openOrdersDataAtom,
  orderBillingDataAtom,
  orderItemsAtom,
  orderShipmentsAtom,
  shopOrdersAtom,
} from '@shared/state/orderState';
import { urlQueryString } from '@shared/lib/util';
import { useTestData } from '@shared/lib/util/site';
import { ISalesOrder, IShopOrder } from '@features/Orders/types';
import {
  activeCustomerDisplayAtom,
  currentCustomerAtom,
  currentCustomerShippingAddressesAtom,
  customerContactsAtom,
  customersAtom,
  customerShippingAddressesAtom,
} from '@shared/state/customerState';
import { devLog } from '@lib/util/logging';
import { purchaseOrdersAtom } from '@shared/state/purchaseOrderState';
import { configToDescription } from '@lib/partParser/util';
import { IConfigTerm, IConfigTermType, ICustomerPart } from '@shared/types/parts';
import { qualityAssuranceTicketsAtom, exceptionCodesAtom } from '@shared/state/qualityAssuranceState';
import { IExceptionCode } from '@features/QualityAssurance/ExceptionCodes/types';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  query,
  where,
  onSnapshot,
} from 'firebase/firestore';
import { ref, get, set } from 'firebase/database';
import { rewriteRealtimePath } from '@services/db/helper';

const DEPENDENCY_TIMEOUT = 30000; // 30 seconds timeout for each dependency

const wrapWithTimeout = async (promise: Promise<any>, name: string) => {
  const timeoutPromise = new Promise((_, reject) => {
    setTimeout(() => reject(new Error(`Dependency ${name} timed out after ${DEPENDENCY_TIMEOUT}ms`)), DEPENDENCY_TIMEOUT);
  });
  return Promise.race([promise, timeoutPromise]);
};

const useDependencies = (dependencyList: string[]) => {
  const { database, firestore } = useFirebase();
  // @ts-ignore
  const { currentUser, userPermissions: authPermissions } = useContext(AuthContext);
  const routeParams = useParams();

  const { pathname, search } = useLocation();
  const [billingData, setBillingData] = useRecoilState(orderBillingDataAtom);
  const [bodyData, setBodyData] = useRecoilState(bodyDataAtom);
  const [currentCustomerShippingAddresses, setCurrentCustomerShippingAddresses] = useRecoilState(currentCustomerShippingAddressesAtom);
  const [customerContacts, setCustomerContacts] = useRecoilState(customerContactsAtom);
  const [customerParts, setCustomerParts] = useRecoilState(customerPartsAtom);
  const [customerShippingAddresses, setCustomerShippingAddresses] = useRecoilState(customerShippingAddressesAtom);
  const [customers, setCustomers] = useRecoilState(customersAtom);
  const [exceptionCodes, setExceptionCodes] = useRecoilState(exceptionCodesAtom);
  const [glCodes, setGlCodes] = useRecoilState(glCodesAtom);
  const [_inventoryItems, setInventoryItems] = useRecoilState(inventoryItemsAtom);
  const inventoryItems = _inventoryItems as unknown as IMaterialSpecification[];
  const [ncCorrectiveActions, setNcCorrectiveActions] = useRecoilState(ncCorrectiveActionsObjectAtom);
  const [ncReasonCodes, setNcReasonCodes] = useRecoilState(ncReasonsObjectAtom);
  const [ncRecords, setNcRecords] = useRecoilState(ncRecordsAtom);
  const [neckData, setNeckData] = useRecoilState(neckDataAtom);
  const [newParts, setNewParts] = useRecoilState(newCustomerPartsAtom);
  const [openOrders, setOpenOrders] = useRecoilState(openOrdersDataAtom);
  const [orderItems, setOrderItems] = useRecoilState(orderItemsAtom);
  const [orders, setOrders] = useRecoilState(shopOrdersAtom);
  const [partConfigTermTypes, setPartConfigTermTypes] = useRecoilState(partConfigTermTypesAtom);
  const [partConfigTerms, setPartConfigTerms] = useRecoilState(partConfigTermsAtom);
  const [partViewerConfigData, setPartViewerConfigData] = useRecoilState(partViewerConfigDataAtom);
  const [productCodes, setProductCodes] = useRecoilState(productCodesAtom);
  const [purchaseOrders, setPurchaseOrders] = useRecoilState(purchaseOrdersAtom);
  const [terms, setTerms] = useRecoilState(termsAtom);
  const [qaTickets, setQaTickets] = useRecoilState(qualityAssuranceTicketsAtom);
  const [routerSteps, setRouterSteps] = useRecoilState(routerStepsAtom);
  const [shopOperators, setShopOperators] = useRecoilState(shopOperatorsObjectAtom);
  const [userSettings, setUserSettings] = useRecoilState(userSettingsAtom);
  const [vendors, setVendors] = useRecoilState(vendorRecordsAtom);
  const customerViewType = useRecoilValue(activeCustomerDisplayAtom);
  const setCurrentCustomer = useSetRecoilState(currentCustomerAtom);
  const setCurrentShopOrder = useSetRecoilState(currentShopOrderAtom);
  const setOrderShipments = useSetRecoilState(orderShipmentsAtom);
  const vendorsDbString = db.vendor.records;
  const lastRouteParams = useRef({});

  // Helper function to determine if a dependency should reload
  const shouldReloadDependency = (depName: string, pathname: string, search: string) => {
    // Only reload route-specific dependencies when the route changes
    const routeSpecificDeps = ['orderBillingData', 'shippingAddresses', 'shopOrderData'];
    
    if (routeSpecificDeps.includes(depName)) {
      // Check if the route parameters that affect this dependency have changed
      const currentParams = qs.parse(search);
      const lastParams = lastRouteParams.current;
      
      console.log(`Checking if ${depName} should reload:`, { currentParams, lastParams, pathname, search });
      
      if (JSON.stringify(currentParams) !== JSON.stringify(lastParams)) {
        lastRouteParams.current = currentParams;
        console.log(`${depName} will reload due to parameter change`);
        return true;
      }
    }
    
    return false;
  };

  const dbDeps = {
    routerSteps: {
      fn: async () => {
        try {
          const rootRef = ref(database, db.realtime.router);
          const snapshot = await wrapWithTimeout(get(rootRef), 'routerSteps');
          const data = snapshot.val();
          if (!data) {
            console.warn('Router steps data is empty');
            setRouterSteps([]);
            return;
          }
          setRouterSteps(data);
          devLog('useDependencies', 161, 'Router steps loaded');
          devLog('useDependencies', 162, data);
        } catch (error) {
          console.error('Failed to load router steps:', error);
          setRouterSteps([]);
        }
      },
      test: () => routerSteps !== null && routerSteps.length > 0,
    },
    configTerms: {
      fn: async () => {
        const terms: Record<string, unknown> = {};
        const termTypes: IConfigTermType[] = [];
        const termTypesRef = collection(getDb('prod'), db.part.config.types);
        const termsRef = collection(getDb('prod'), db.part.config.terms);
        const termTypesDocs = await getDocs(termTypesRef);
        const termsDocs = await getDocs(termsRef);
        
        termTypesDocs.forEach((d) => {
          termTypes.push({ ...d.data(), id: d.id } as IConfigTermType);
        });
        termsDocs.forEach((d) => {
          const termData = d.data();
          terms[d.id] = termData;
        });
        setPartConfigTerms(terms as unknown as IConfigTerm[]);
        setPartConfigTermTypes(termTypes as IConfigTermType[]);
      },
      test: () => partConfigTermTypes.length > 0 && Object.keys(partConfigTerms).length > 0,
    },
    customers: {
      fn: async () => {
        const includeInactive = includes(['all', 'inactive'], customerViewType);
        devLog('useDependencies', 115, `Customer view type: ${customerViewType}`);
        devLog('useDependencies', 115, `Show inactive customers: ${includeInactive}`);
        devLog('useDependencies', 115, routeParams);
        const customersRef = collection(db.firestore, db.customer.records);
        const customerDocs = await getDocs(customersRef);
        const _customers = customerDocs.docs.map((c) => c.data() as ICustomerRecord)
          .filter((c: ICustomerRecord) => c.active || includeInactive);
        setCustomers(sortBy(_customers, (d: ICustomerRecord) => d.DisplayName));

        const isCustomerAccount = authPermissions
          && !authPermissions.permissions.includes('*')
          && !authPermissions.permissions.includes('customers.pricing');

        if (isCustomerAccount) {
          const login = currentUser.email.split('@')[0];
          const cust = find(_customers, (c: ICustomerRecord) => c.login === login);
          if (cust) setCurrentCustomer(cust);
        } else if (localStorage.getItem('currentCustomerId')) {
          const localId = localStorage.getItem('currentCustomerId');
          const cust = find(_customers, (c: ICustomerRecord) => c.id === localId);
          if (cust) setCurrentCustomer(cust);
        }
        devLog('useDependencies', 166, 'Customers loaded');
      },
      test: () => customers.length > 0,
    },
    newCustomerParts: {
      fn: async () => {
        const partsRef = collection(db.firestore, db.part.records);
        const partsQuery = query(partsRef, where('new', '==', true));
        onSnapshot(partsQuery, (snapshot) => {
          const data: ICustomerPart[] = snapshot.docs.map((d) => d.data() as ICustomerPart);
          setNewParts(data);
        });
      },
      test: () => newParts.length > 0,
    },
    ncConfig: {
      fn: async () => {
        const ncConfigRef = ref(database, '/ncConfig');
        const snapshot = await get(ncConfigRef);
        const data = snapshot.val();
        setNcCorrectiveActions(data.correctiveActions);
        setNcReasonCodes(data.reasonCodes);
      },
      test: () => (Object.values(ncCorrectiveActions).length > 0 && Object.values(ncReasonCodes).length > 0),
    },
    customerContacts: {
      fn: async () => {
        const contactsRef = collection(db.firestore, db.customer.contacts);
        const snap = await getDocs(contactsRef);
        const data: IContact[] = [];
        snap.forEach((doc) => {
          data.push(doc.data().contacts as IContact);
        });
        setCustomerContacts(flatten(data));
      },
      test: () => customerContacts.length > 0,
    },
    glCodes: {
      fn: async () => {
        const glCodesRef = collection(db.firestore, db.accounting.glCodes);
        const glCodeDocs = await getDocs(glCodesRef);
        const data: IGlCode[] = glCodeDocs.docs.map((g) => g.data() as IGlCode);
        setGlCodes(data);
        devLog('useDependencies', 186, 'GL Codes loaded');
        devLog('useDependencies', 187, data);
      },
      test: () => glCodes.length > 0,
    },
    inventory: {
      fn: async () => {
        if (inventoryItems !== null && inventoryItems.length > 0) {
          devLog('useDependencies', 275, 'Inventory already loaded, skipping DB call.');
          return;
        }
        try {
          const inventoryRef = collection(db.firestore, db.inventory.records);
          const inventoryDocs = await getDocs(inventoryRef);
          const inventoryData = inventoryDocs.docs.map((d) => d.data() as IMaterialSpecification);
          
          const batchSize = 50;
          const processedItems: IMaterialSpecification[] = [];

          for (let i = 0; i < inventoryData.length; i += batchSize) {
            const batch = inventoryData.slice(i, i + batchSize);

          const processedBatch = batch.map((item) => {
            try {
              // Precompute search strings for main item
              const mainItemSearchString = `${item.Description || ''}${item.Sku || ''}${(item.MaterialSellPrice || 0).toString()}${item.ProductCode || ''}${item.PurchaseDetails?.Vendor || ''}${item.Type || ''}`.toLowerCase();
              
              const processedExistingSourceItems = (item.SourceItems || []).map((sourceItem) => {
                const sourceItemSearchString = `${sourceItem.Description || ''}${sourceItem.Sku || ''}${(sourceItem.PurchaseCost || 0).toString()}${sourceItem.Vendor || ''}${item.ProductCode || ''}`.toLowerCase();
                return {
                  ...sourceItem,
                  StandardCost: item.StandardCost,
                  _searchString: sourceItemSearchString,
                  parentDescription: item.Description,
                };
              });
              
              
              return { 
                ...item, 
                SourceItems: processedExistingSourceItems,
                _searchString: mainItemSearchString,
                UnitPrice: item.MaterialSellPrice + item.LaborSellPrice 
              };
            } catch (error) {
                console.error(`Error processing inventory item ${item.Sku}:`, error);
                return { 
                    ...item, 
                    _searchString: `${item.Description || ''}${item.Sku || ''}${(item.MaterialSellPrice || 0).toString()}${item.ProductCode || ''}${item.PurchaseDetails?.Vendor || ''}${item.Type || ''}`.toLowerCase(),
                    UnitPrice: item.MaterialSellPrice + item.LaborSellPrice,
                }; 
            }
          });

            processedItems.push(...processedBatch);

            await new Promise(resolve => setTimeout(resolve, 0));
          }

          setInventoryItems(processedItems as unknown as import('@shared/types/dbRecords').IInventoryPart[]);
          devLog('useDependencies', 357, 'Inventory loaded efficiently');

        } catch (error) {
          console.error('Failed to load inventory:', error);
          setInventoryItems([]);
        }
      },
      test: () => inventoryItems !== null && inventoryItems.length > 0,
    },
    purchaseOrders: {
      fn: async () => {
        if (purchaseOrders.length > 0) {
          devLog('useDependencies', 372, 'Purchase orders already loaded, skipping DB call.');
          return;
        }
        const poRef = collection(db.firestore, db.purchaseOrder.records);
        const snapshot = await getDocs(poRef);
        const data: IPurchaseOrder[] = snapshot.docs.map((d) => ({
          ...d.data() as IPurchaseOrder,
          _totalValue: (d.data().Items || []).reduce((acc, item) => acc + (item.total), 0)
        }));
        if (!data.length) setPurchaseOrders([]);
        setPurchaseOrders(data);
        devLog('useDependencies', 208, 'Purchase Orders loaded');
      },
      test: () => purchaseOrders.length > 0,
    },
    openOrders: {
      fn: async () => {
        const ordersRef = collection(db.firestore, db.order.records);
        const ordersQuery = query(ordersRef, where('completed', '!=', true));
        const orderDocs = await getDocs(ordersQuery);
        
        const _orders = await Promise.all(orderDocs.docs.map(async (d) => {
          const data = d.data() as IShopOrder;
          const itemDocRef = doc(db.firestore, db.order.items, d.id);
          const itemDoc = await getDoc(itemDocRef);
          const _orderItems = itemDoc.exists() ? (itemDoc.data()?.orderItems || []) as IOrderItem[] : [];
          return { ...data, _orderItems };
        }));
        setOpenOrders(_orders);
      },
      test: () => openOrders.length > 0,
    },
    allOrders: {
      fn: async () => {
        const ordersRef = collection(db.firestore, db.order.records);
        const snapshot = await getDocs(ordersRef);
        const data: ISalesOrder[] = [];
        snapshot.forEach((d) => {
          data.push(d.data() as ISalesOrder);
        });
        setOrders(data);
      },
      test: () => orders.length > 0,
    },
    productCodes: {
      fn: async () => {
        const productCodesRef = collection(db.firestore, db.accounting.productCodes);
        const productCodeDocs = await getDocs(productCodesRef);
        const data: IProductCode[] = productCodeDocs.docs.map((p: any) => p.data() as IProductCode);
        setProductCodes(data);
        devLog('useDependencies', 243, 'Product Codes loaded');
        devLog('useDependencies', 244, data);
      },
      test: () => productCodes.length > 0,
    },
    partData: {
      fn: async () => {
        devLog('useDependencies', 250, 'Loading part data');
        const rootRef = ref(database, '/');
        const snapshot = await get(rootRef);
        const data =  snapshot.val();
        const bData = data[db.realtime.body];
        const nData = data[db.realtime.neck];
        setBodyData(bData);
        setNeckData(nData);
      },
      test: () => Object.keys(bodyData).length > 0 && Object.keys(neckData).length > 0,
    },
    operatorData: {
      fn: async () => {
        const operatorsRef = ref(database, rewriteRealtimePath(db.realtime.operators, 'prod'));
        const operatorsSnapshot = await get(operatorsRef);
        const operators = operatorsSnapshot.val();
        setShopOperators(operators);
      },
      test: () => !!shopOperators.length,
    },
    orderBillingData: {
      fn: async () => {
        const currentOrderId = qs.parse(window.location.href.replace('?', '')).orderId;
        if (currentOrderId === 'ph') {
          setBillingData(false);
          return;
        }
        if (currentOrderId) {
          const orderDocRef = doc(db.firestore, db.order.records, currentOrderId as string);
          const orderDoc = await getDoc(orderDocRef);
          if (!orderDoc.exists()) setBillingData(false);
          const id = orderDoc.data()?.customer.id;

          accessToken().then((token) => {
            orderBillingAddress(token, id)
              .then((res) => {
                setBillingData(res as any);
                devLog('useDependencies', 282, 'Billing info loaded');
              })
              .catch(() => setBillingData(false));
          });
        }
      },
      test: () => billingData !== null,
    },
    shopOrderData: {
      fn: async () => {
        const currentOrderId = qs.parse(window.location.href.replace('?', '')).orderId;
        if (currentOrderId) {
          const shopOrderRef = doc(db.firestore, db.order.records, currentOrderId as string);
          const shopOrderDoc = await getDoc(shopOrderRef);
          const shopOrderData = shopOrderDoc.data();
          
          if (!shopOrderDoc.exists() || !shopOrderData) {
            setOrderItems([null]);
            return;
          }
          
          setCurrentShopOrder(shopOrderData as IShopOrder);
          setCurrentCustomer(shopOrderData.customer);

          const partsRef = collection(db.firestore, db.part.records);
          const customerId = shopOrderData.customer.DisplayName.match(/CASH[C]?/) ? 'WILDW' : shopOrderData.customer.id;
          const partsQuery = query(partsRef, where('customer', '==', customerId));
          const customerPartDocs = await getDocs(partsQuery);
          
          setCustomerParts(customerPartDocs.docs.map((d) => {
            const partData = d.data();
            return { ...partData, Description: configToDescription(partData.config) };
          }));

          const orderItemRef = doc(db.firestore, db.order.items, shopOrderDoc.id);
          const orderItemDoc = await getDoc(orderItemRef);
          const itemsData = orderItemDoc.data();
          
          if (!orderItemDoc.exists() || !itemsData || Object.keys(itemsData).length === 0 || itemsData.orderItems.filter((i: IOrderItem) => i).length === 0) {
            setOrderItems([null]);
          } else {
            const orderItemData = itemsData.orderItems
              .filter((i: IOrderItem) => i)
              .map((i: IOrderItem) => ({
                ...i,
                Sku: i.Sku,
                Description: i.Description,
                quantityOpen: i.quantityOpen,
              }));
            setOrderItems(orderItemData);
            devLog('useDependencies', 338, 'shop order items loaded');
          }
          const shippingAddressRef = doc(db.firestore, db.customer.shippingAddresses, shopOrderData.customer.DisplayName);
          const shippingAddressesDoc = await getDoc(shippingAddressRef);
          const shippingAddresses = shippingAddressesDoc.data()?.shippingAddresses;
          if (!shippingAddressesDoc.exists() || !shippingAddresses || shippingAddresses.filter((i: IShippingAddress) => i).length === 0) {
            setCurrentCustomerShippingAddresses([]);
          } else {
            setCurrentCustomerShippingAddresses(shippingAddresses);
            devLog('useDependencies', 374, `shipping addresses for ${shopOrderData.customer.DisplayName} loaded`);
          }

          const shipmentsRef = collection(db.firestore, db.order.shipments);
          const shipmentsQuery = query(shipmentsRef, where('orderId', '==', currentOrderId));
          const orderShipmentsDoc = await getDocs(shipmentsQuery);
          const orderShipments = orderShipmentsDoc.docs.map((d) => d.data() as IShipment);
          setOrderShipments(orderShipments);
        } else {
          devLog('useDependencies', 376, 'No order data found, exiting dependency loader');
          setOrderItems([null]);
        }
      },
      test: () => orderItems && !!orderItems.length,
    },
    shippingAddresses: {
      fn: async () => {
        const { customer } = qs.parse(urlQueryString(window.location.href));
        const shippingAddressRef = doc(db.firestore, db.customer.shippingAddresses, customer as string);
        const shippingAddressesDoc = await getDoc(shippingAddressRef);
        const shippingAddresses = shippingAddressesDoc.data()?.shippingAddresses;
        if (!shippingAddressesDoc.exists() || !shippingAddresses || shippingAddresses.filter((i: IOrderItem) => i).length === 0) {
          setCurrentCustomerShippingAddresses([null]);
        } else {
          setCurrentCustomerShippingAddresses(shippingAddresses as IShippingAddress[]);
          devLog('useDependencies', 338, `shipping addresses for ${customer} loaded`);
        }
      },
      test: () => !!currentCustomerShippingAddresses.length,
    },
    qualityAssuranceTickets: {
      fn: async () => {
        const qaTicketsRef = collection(db.firestore, db.qualityAssurance.tickets);
        const qaTicketDocs = await getDocs(qaTicketsRef);
        const qaTicketData = qaTicketDocs.docs.map((d: any) => d.data() as IQATicket);
        setQaTickets(qaTicketData);
      },
      test: () => qaTickets.length > 0,
    },
    exceptionCodes: {
      fn: async () => {
        const exceptionCodesRef = collection(db.firestore, db.qualityAssurance.exceptionCodes);
        const exceptionCodesDocs = await getDocs(exceptionCodesRef);
        const exceptionCodesData = exceptionCodesDocs.docs.map((d: any) => d.data() as IExceptionCode);
        setExceptionCodes(exceptionCodesData);
      },
      test: () => exceptionCodes.length > 0,
      error: () => {
        console.error('Error loading exception codes');
        return false;
      },
    },
    vendors: {
      fn: async () => {
        const vendorsRef = collection(db.firestore, vendorsDbString);
        const snapshot = await getDocs(vendorsRef);
        const data: IVendor[] = [];
        snapshot.forEach((v: any) => {
          data.push(v.data() as IVendor);
        });
        setVendors(data);
      },
      test: () => vendors.length > 0,
    },
    terms: {
      fn: async () => {
        const termsRef = collection(db.firestore, db.accounting.terms);
        const termsDocs = await getDocs(termsRef);
        const data: ITerm[] = termsDocs.docs.map((d) => d.data() as ITerm);
        setTerms(data);
        devLog('useDependencies', 570, 'Payment terms loaded');
      },
      test: () => terms.length > 0,
    },
    settings: {
      fn: async () => {
        try {
          devLog('useDependencies', 461, 'Loading settings');
          if (!currentUser?.email) {
            console.warn('No current user email available for settings load');
            setUserSettings(USER_SETTINGS_DEFAULTS);
            return;
          }

          // Customer users get default settings (no Firebase lookup)
          const isCustomerSettingsAccount = authPermissions
            && !authPermissions.permissions.includes('*')
            && !authPermissions.permissions.includes('customers.pricing');
          if (isCustomerSettingsAccount) {
            setUserSettings(USER_SETTINGS_DEFAULTS);
            return;
          }

          // Admin/shop users get settings from Firebase
          const userKey = currentUser.email.split('@')[0];
          const settingsRef = ref(database, `/settings/${userKey}`);
          
          try {
            const snapshot = await wrapWithTimeout(get(settingsRef), 'settings');
            const data = snapshot.val();
            
            if (data) {
              devLog('useDependencies', 473, 'Loading user settings from Firebase');
              // Merge with defaults to ensure all keys exist
              setUserSettings({
                boolean: { ...USER_SETTINGS_DEFAULTS.boolean, ...data.boolean },
                string: { ...USER_SETTINGS_DEFAULTS.string, ...data.string },
              });
              return;
            }
            
            // No settings in Firebase yet, create them with defaults
            await set(settingsRef, USER_SETTINGS_DEFAULTS);
            setUserSettings(USER_SETTINGS_DEFAULTS);
          } catch (error) {
            console.error('Error loading settings:', error);
            setUserSettings(USER_SETTINGS_DEFAULTS);
          }
        } catch (error) {
          console.error('Settings load failed:', error);
          setUserSettings(USER_SETTINGS_DEFAULTS);
        }
      },
      test: () => userSettings !== null,
    },
  };

  // Modify the depsReady function to be more lenient
  const depsReady = () => {
    try {
      return every(dependencyList.map((d: string) => {
        const dep = dbDeps[d];
        if (!dep) {
          console.warn(`Unknown dependency: ${d}`);
          return true; // Don't block on unknown dependencies
        }
        return dep.test();
      }));
    } catch (error) {
      console.error('Error checking dependencies:', error);
      return false;
    }
  };

  useEffect(() => {
    if (!currentUser) return;
    
    const loadDependencies = async () => {
      for (const dep of dependencyList) {
        try {
          if (dbDeps[dep]) {
            if (dbDeps[dep].test && dbDeps[dep].test()) {
              devLog('useDependencies', 753, `Dependency ${dep} already satisfied, skipping fn.`);
              continue;
            } else {
              // First time loading this dependency
              devLog('useDependencies', 753, `Loading dependency ${dep}`);
              await dbDeps[dep].fn();
            }
          }
        } catch (error) {
          console.error(`Error loading dependency ${dep}:`, error);
        }
      }
    };

    loadDependencies();
  }, [currentUser, dependencyList]);

  return depsReady;
};

export default useDependencies;
